import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { PILLARS, calculateNextIndex } from '../utils/scoring';

export default function ScoreHistory({ audits }) {
  if (!audits || audits.length < 2) return null;

  // Sort oldest first
  const sorted = [...audits].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // Build chart data
  const data = sorted.map((audit, i) => {
    const entry = {
      name: `Audit ${i + 1}`,
      date: new Date(audit.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }),
      'Next Index': calculateNextIndex(audit.scores || {}),
    };
    PILLARS.forEach(p => {
      entry[p.name] = audit.scores?.[p.id]?.score || 0;
    });
    return entry;
  });

  const COLORS = ['#C9A668', '#f87171', '#60a5fa', '#c084fc', '#34d399', '#fb923c', '#fbbf24', '#06b6d4', '#a78bfa', '#f472b6'];

  // Calculate overall change
  const firstIndex = data[0]['Next Index'];
  const lastIndex = data[data.length - 1]['Next Index'];
  const delta = lastIndex - firstIndex;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">Score History</h3>
          <p className="text-xs text-neutral-500">{sorted.length} audits over time</p>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-black ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-neutral-400'}`}>
            {delta > 0 ? '+' : ''}{delta.toFixed(1)}
          </p>
          <p className="text-[10px] text-neutral-500">Overall Change</p>
        </div>
      </div>

      <div style={{ height: 300 }}>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" tick={{ fill: '#a3a3a3', fontSize: 10 }} />
            <YAxis domain={[0, 10]} tick={{ fill: '#525252', fontSize: 10 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#171717', border: '1px solid #333', borderRadius: 8, fontSize: 11 }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line
              type="monotone"
              dataKey="Next Index"
              stroke="#C9A668"
              strokeWidth={3}
              dot={{ fill: '#C9A668', r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Pillar movement table */}
      {sorted.length >= 2 && (
        <div className="mt-4">
          <p className="text-xs text-neutral-500 mb-2">Pillar Movement (first → latest)</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {PILLARS.map((p, i) => {
              const first = sorted[0].scores?.[p.id]?.score || 0;
              const last = sorted[sorted.length - 1].scores?.[p.id]?.score || 0;
              const change = last - first;
              return (
                <div key={p.id} className="bg-neutral-800/30 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-neutral-500 truncate mb-1">{p.name}</p>
                  <p className={`text-sm font-bold ${change > 0 ? 'text-emerald-400' : change < 0 ? 'text-red-400' : 'text-neutral-500'}`}>
                    {change > 0 ? '↑' : change < 0 ? '↓' : '='}{Math.abs(change).toFixed(1)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
